import { __read, __assign } from '../../external/tslib/tslib.es6.mjs.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useState, useMemo, useEffect } from 'react';
import { useTimer } from 'react-timer-hook';
import { useIdle } from 'react-use';
import clsx from 'clsx';
import { ClockIcon } from '../../icons/ClockIcon.js';
import { Modal } from '../modal/Modal.js';
import { Button } from '../button/Button.js';
import styles from './SessionTimeoutWarning.module.scss.js';

/**
 * Displays a modal with a session expiration message after a specified amount of user idle time.
 */
var SessionTimeoutWarning = function (_a) {
    var idleTime = _a.idleTime, timeoutDuration = _a.timeoutDuration, onTimeoutExpired = _a.onTimeoutExpired, onExtendSession = _a.onExtendSession, onModalDisplayed = _a.onModalDisplayed, className = _a.className;
    var _b = __read(useState(false), 2), showWarning = _b[0], setShowWarning = _b[1];
    var timeoutDurationLabel = useMemo(function () { return formatTimeLong(Math.floor(timeoutDuration / 1000 / 60), Math.floor(timeoutDuration / 1000) % 60); }, [timeoutDuration]);
    // This countdown timer is displayed on the dialog so the user knows how much time they have left in their session.
    var timer = useTimer({
        expiryTimestamp: new Date(),
        onExpire: onTimeoutExpired,
        autoStart: false,
    });
    var roundedTime = useMemo(function () { return getTimeToNearestTenSeconds(timer); }, [timer]);
    var extendSession = function () {
        setShowWarning(false);
        onExtendSession === null || onExtendSession === void 0 ? void 0 : onExtendSession();
        onModalDisplayed === null || onModalDisplayed === void 0 ? void 0 : onModalDisplayed(false);
        timer.pause();
    };
    var isUserIdle = useIdle(idleTime);
    useEffect(function () {
        if (isUserIdle && !showWarning) {
            setShowWarning(true);
            timer.restart(getExpirationDate(timeoutDuration));
            onModalDisplayed === null || onModalDisplayed === void 0 ? void 0 : onModalDisplayed(true);
        }
    }, [timer, isUserIdle, showWarning, timeoutDuration, onModalDisplayed]);
    return showWarning ? (jsxs(Modal, __assign({ size: "s", isOpened: true, "aria-labelledby": "session-timeout-warning", className: clsx(className) }, { children: [jsx(Modal.Header, __assign({ id: "session-timeout-warning" }, { children: "Your session is about to expire" })), jsxs(Modal.Content, __assign({ className: styles.content }, { children: [jsxs("p", { children: ["For your security, this session will be timed out in ", timeoutDurationLabel, " due to inactivity. To continue, select \"Extend session\" to stay signed in."] }), jsxs("p", { children: [jsxs("time", __assign({ role: "timer", "aria-hidden": "true", dateTime: "PT".concat(timer.minutes, "M").concat(timer.seconds, "S") }, { children: [jsx(ClockIcon, { className: styles.clockIcon }), jsxs("span", __assign({ className: styles.timeLeft }, { children: [formatTime(timer), " left"] }))] })), jsx("time", { className: "sr-only", role: "timer", "aria-live": "assertive", "aria-label": "Your session will timeout in ".concat(formatTimeLong(roundedTime.minutes, roundedTime.seconds)), dateTime: "PT".concat(roundedTime.minutes, "M").concat(roundedTime.seconds, "S") })] })] })), jsx(Modal.Actions, { children: jsx(Button, __assign({ onClick: extendSession, size: "m", className: styles.actionButton }, { children: "Extend session" })) })] }))) : null;
};
function getExpirationDate(timeoutDuration) {
    var date = new Date();
    date.setMilliseconds(date.getMilliseconds() + timeoutDuration);
    return date;
}
function formatTime(timer) {
    return "".concat(timer.minutes, ":").concat(timer.seconds < 10 ? '0' : '').concat(timer.seconds);
}
function formatTimeLong(minutes, seconds) {
    var parts = [];
    if (minutes > 0)
        parts.push("".concat(minutes, " minutes"));
    if (seconds > 0)
        parts.push("".concat(seconds, " seconds"));
    return parts.join(' ');
}
function getTimeToNearestTenSeconds(timer) {
    var seconds = Math.ceil(timer.seconds / 10) * 10;
    return {
        seconds: seconds % 60,
        minutes: timer.minutes + (seconds === 60 ? 1 : 0),
    };
}

export { SessionTimeoutWarning };
